import { useParams, Navigate } from 'react-router-dom'
import { Paragraph } from '@quickmd-llc/design-system'
import { MEDICATIONS_PAGE_ROUTE } from '@/utils/constants'
import { useGetAdminsMedications } from '@/api/generated/medication/medication'
import { useUpdateMedication } from './hooks/useUpdateMedication'
import { MedicationFormPage } from './components/MedicationForm/MedicationFormPage'
import type { MedicationFormValues } from './types'

export function EditMedicationPage() {
  const { id } = useParams<{ id: string }>()
  const { data: medications, isLoading } = useGetAdminsMedications()
  const { mutateAsync, isPending } = useUpdateMedication()

  if (isLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Paragraph size='md'>Loading...</Paragraph>
      </div>
    )
  }

  const medication = medications?.find((m) => m.Id === id)

  if (!medication) {
    return <Navigate to={MEDICATIONS_PAGE_ROUTE} replace />
  }

  const formulations = medication.Formulations ?? []
  const defaultValues: Partial<MedicationFormValues> = {
    drugName: medication.Name ?? '',
    displayDescription: medication.PatientDescription ?? '',
    medicationImage: null,
    gcnEnabled: medication.GenericCodeNumber !== null && medication.GenericCodeNumber !== undefined,
    gcn: medication.GenericCodeNumber ?? '',
    ndcEnabled: medication.NationalDrugCode !== null && medication.NationalDrugCode !== undefined,
    ndc: medication.NationalDrugCode ? medication.NationalDrugCode.replace(/\D/g, '') : '',
    otherEnabled: medication.AlternativeThirdPartyKey !== null && medication.AlternativeThirdPartyKey !== undefined,
    otherDrugCode: medication.AlternativeThirdPartyKey ?? '',
    unit: medication.DispenseUnit ?? 'Tablets',
    // Cast is a no-op once Orval regenerates with Category: string.
    category: (medication.Category as unknown as string) ?? '',
    pricingTiers:
      formulations.length > 0 ?
        formulations.map((f) => ({ quantity: f.TotalQuantity, patientPrice: f.Price }))
      : [{ quantity: 30, patientPrice: 0 }],
    isControlledSubstance: medication.IsControlledSubstance,
    fulfillmentPartnerIds: medication.FulfillmentPartnerNcpdpIds ?? [],
  }

  const medicationId = medication.Id ?? undefined
  const existingImageUrl = medication.ImageUrl
  async function handleSubmit(values: MedicationFormValues) {
    if (!medicationId) return undefined
    await mutateAsync({ ...values, id: medicationId, existingImageUrl })
    return medicationId
  }

  return (
    <MedicationFormPage
      mode='edit'
      defaultValues={defaultValues}
      existingImageUrl={medication.ImageUrl}
      medicationId={medicationId}
      successMessage='Medication saved!'
      onSubmit={handleSubmit}
      isPending={isPending}
    />
  )
}
